import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { ClickSparkWrapper } from "./reactbits"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Custom MSB variants
        childPrimary: "bg-child-primary hover:bg-child-primary/90 text-white font-medium py-3 px-6 rounded-xl transition-all transform hover:scale-105 focus:outline-hidden focus:ring-2 focus:ring-child-primary/50 font-child",
        childSecondary: "bg-child-secondary hover:bg-child-secondary/90 text-white font-medium py-3 px-6 rounded-xl transition-all transform hover:scale-105 focus:outline-hidden focus:ring-2 focus:ring-child-secondary/50 font-child",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  success?: boolean
  confetti?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, success, confetti, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Check if this is a child-themed button
    const isChildButton = variant === "childPrimary" || variant === "childSecondary"

    const buttonElement = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      />
    )
    
    // Wrap child buttons with ClickSpark for interactive feedback
    if (isChildButton && !loading && !props.disabled) {
      return <ClickSparkWrapper>{buttonElement}</ClickSparkWrapper>
    }
    
    return buttonElement
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
